import {
  Component,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ElementRef,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import {
  LocationLookupService,
  LocationSuggestionGroups,
  LocationSuggestionItem,
} from '../../services/location-lookup.service';

export type LocationSelectionType =
  | 'region'
  | 'subregion'
  | 'propertyType'
  | 'property'
  | 'mapping'
  | 'keyword';

export interface LocationSelection {
  type: LocationSelectionType;
  id?: number;
  name: string;
  sublabel?: string;
}

type SuggestionGroupKey =
  | 'subregions'
  | 'mappings'
  | 'regions'
  | 'keywordSuggestions'
  | 'propertyTypes'
  | 'properties';

@Component({
  selector: 'app-location-typeahead',
  templateUrl: './location-typeahead.component.html',
  styleUrls: ['./location-typeahead.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LocationTypeaheadComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class LocationTypeaheadComponent
  implements ControlValueAccessor, OnDestroy
{
  @ViewChild('inputWrap') inputWrap?: ElementRef<HTMLElement>;
  @ViewChild('inputElement') inputElement?: ElementRef;
  @ViewChild('suggestionsTemplate') suggestionsTemplate?: TemplateRef<void>;
  @Input() placeholder = 'Where are you going?';
  @Input() minChars = 2;
  @Input() maxItems = 6;
  @Input() bordered = false;
  @Input() emptyStateSuggestions: LocationSelection[] = [];
  @Input() renderSuggestionsInline = true;
  @Output() locationSelected = new EventEmitter<LocationSelection>();
  @Output() firstSuggestionChange =
    new EventEmitter<LocationSelection | null>();

  private overlayRef?: OverlayRef;

  value = '';
  suggestionGroups: LocationSuggestionGroups = {
    regions: [],
    subregions: [],
    propertyTypes: [],
    properties: [],
    mappings: [],
    keywordSuggestions: [],
    keyword: null,
  };
  showSuggestions = false;
  isLoading = false;
  activeIndex = -1;

  private readonly suggestionOrder: SuggestionGroupKey[] = [
    'subregions',
    'mappings',
    'regions',
    'keywordSuggestions',
    'propertyTypes',
    'properties',
  ];

  private readonly inputChanges$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private locationLookupService: LocationLookupService,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
  ) {
    this.inputChanges$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((keywords) => {
          if (!keywords || keywords.trim().length < this.minChars) {
            this.isLoading = false;
            this.resetSuggestions();
            return of(this.emptyGroups());
          }

          this.isLoading = true;
          return this.locationLookupService
            .searchTerms(keywords.trim())
            .pipe(finalize(() => (this.isLoading = false)));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((results) => {
        const mergedKeywordSuggestions = this.mergeKeywordSuggestions(
          results.keywordSuggestions || [],
        );

        this.suggestionGroups = {
          regions: (results.regions || []).slice(0, this.maxItems),
          subregions: (results.subregions || []).slice(0, this.maxItems),
          propertyTypes: (results.propertyTypes || []).slice(0, this.maxItems),
          properties: (results.properties || []).slice(0, this.maxItems),
          mappings: (results.mappings || []).slice(0, this.maxItems),
          keywordSuggestions: mergedKeywordSuggestions,
          keyword: results.keyword || this.value.trim(),
        };

        this.showSuggestions =
          this.hasAnySuggestions() || this.canShowKeyword();
        this.activeIndex = -1;
        this.firstSuggestionChange.emit(this.getFlatSuggestions()[0] ?? null);

        // Open overlay when suggestions become available (only in CDK mode)
        if (
          !this.renderSuggestionsInline &&
          this.showSuggestions &&
          this.inputElement &&
          this.suggestionsTemplate &&
          !this.overlayRef
        ) {
          this.openOverlay();
        }
      });
  }

  writeValue(value: string): void {
    this.value = value || '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(_isDisabled: boolean): void {}

  onInput(value: string): void {
    this.value = value;
    this.onChange(value);
    this.activeIndex = -1;

    if (!value || value.trim().length < this.minChars) {
      if (!value?.trim().length && this.emptyStateSuggestions.length > 0) {
        this.applyEmptyStateSuggestions();
        this.isLoading = false;
        return;
      }

      this.resetSuggestions();
      this.isLoading = false;
      return;
    }

    // Show keyword fallback immediately so users always see options,
    // even if the API response is delayed.
    this.showSuggestions = this.canShowKeyword();
    if (
      !this.renderSuggestionsInline &&
      this.showSuggestions &&
      this.inputElement &&
      this.suggestionsTemplate &&
      !this.overlayRef
    ) {
      this.openOverlay();
    }

    this.inputChanges$.next(value || '');
  }

  clearInput(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.value = '';
    this.onChange('');
    this.onTouched();
    this.isLoading = false;
    this.activeIndex = -1;

    if (this.emptyStateSuggestions.length > 0) {
      this.applyEmptyStateSuggestions();
    } else {
      this.resetSuggestions();
    }

    this.focusInput();
  }

  focusInput(): void {
    setTimeout(() => {
      this.inputElement?.nativeElement?.focus();
    }, 50);
  }

  onFocus(): void {
    if (!this.value?.trim().length && this.emptyStateSuggestions.length > 0) {
      this.applyEmptyStateSuggestions();
    }

    this.showSuggestions = this.hasAnySuggestions() || this.canShowKeyword();
    if (
      this.renderSuggestionsInline ||
      !this.showSuggestions ||
      !this.inputElement ||
      !this.suggestionsTemplate ||
      this.overlayRef
    ) {
      return;
    }
    this.openOverlay();
  }

  onBlur(): void {
    this.onTouched();

    // In inline mode, keep the list visible under the input instead of
    // collapsing on blur/outside click.
    if (this.renderSuggestionsInline) {
      return;
    }

    setTimeout(() => {
      this.showSuggestions = false;
      this.closeOverlay();
    }, 120);
  }

  private openOverlay(): void {
    if (!this.inputElement || !this.suggestionsTemplate || this.overlayRef) {
      return;
    }

    const anchorElement =
      this.inputWrap?.nativeElement || this.inputElement.nativeElement;
    const anchorWidth = anchorElement.getBoundingClientRect().width;

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(anchorElement)
      .withLockedPosition(false)
      .withPush(true)
      .withViewportMargin(8)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 6,
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetY: -6,
        },
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      panelClass: 'location-typeahead-overlay-pane',
      width: anchorWidth,
      minWidth: anchorWidth,
      maxWidth: anchorWidth,
    });

    const portal = new TemplatePortal(
      this.suggestionsTemplate,
      this.viewContainerRef,
    );
    this.overlayRef.attach(portal);
  }

  private closeOverlay(): void {
    if (this.overlayRef) {
      this.overlayRef.detach();
      this.overlayRef = undefined;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.renderSuggestionsInline) {
      return;
    }

    const target = event.target as HTMLElement;
    if (!target.closest('.location-typeahead')) {
      this.showSuggestions = false;
      this.activeIndex = -1;
      this.closeOverlay();
    }
  }

  onArrowDown(event: Event): void {
    event.preventDefault();
    if (!this.showSuggestions) {
      return;
    }

    const total = this.getFlatSuggestions().length;
    if (total === 0) {
      return;
    }

    this.activeIndex = Math.min(this.activeIndex + 1, total - 1);
    this.scrollActiveIntoView();
  }

  onArrowUp(event: Event): void {
    event.preventDefault();
    if (!this.showSuggestions) {
      return;
    }

    this.activeIndex = Math.max(this.activeIndex - 1, -1);
    this.scrollActiveIntoView();
  }

  onEscape(): void {
    this.showSuggestions = false;
    this.activeIndex = -1;
    this.closeOverlay();
  }

  onEnter(event: Event): void {
    event.preventDefault();

    if (!this.showSuggestions) {
      return;
    }

    const items = this.getFlatSuggestions();

    if (this.activeIndex >= 0 && this.activeIndex < items.length) {
      this.selectSuggestion(items[this.activeIndex]);
      return;
    }

    // For numeric ID queries, prefer the first property result so pressing
    // Enter behaves like a property-ID lookup instead of a generic keyword search.
    if (this.isLikelyPropertyIdQuery(this.value)) {
      const firstProperty = items.find((item) => item.type === 'property');
      if (firstProperty) {
        this.selectSuggestion(firstProperty);
        return;
      }
    }

    if (items.length === 1) {
      this.selectSuggestion(items[0]);
    }
  }

  selectSuggestion(suggestion: LocationSelection): void {
    this.value = suggestion.name;
    this.onChange(suggestion.name);
    this.showSuggestions = false;
    this.isLoading = false;
    this.activeIndex = -1;
    this.suggestionGroups = this.emptyGroups();
    this.closeOverlay();
    this.firstSuggestionChange.emit(suggestion);
    this.locationSelected.emit(suggestion);
  }

  private scrollActiveIntoView(): void {
    if (this.activeIndex < 0) {
      return;
    }

    setTimeout(() => {
      // Prefer current component's list to support keyboard navigation
      // when multiple typeaheads exist on the page.
      const localContainer = this.inputWrap?.nativeElement
        ?.closest('.location-typeahead')
        ?.querySelector('.location-suggestions') as HTMLElement | null;

      // Fallback for non-inline/overlay rendering.
      const container =
        localContainer ||
        (document.querySelector('.location-suggestions') as HTMLElement | null);

      if (!container) {
        return;
      }

      const activeButton = container.querySelector(
        '.location-suggestion-item.active',
      ) as HTMLElement;

      if (activeButton) {
        activeButton.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    }, 0);
  }

  getFlatSuggestions(): LocationSelection[] {
    const items: LocationSelection[] = [];

    this.suggestionGroups.subregions.forEach((s) => {
      items.push({
        type: 'subregion',
        id: s.id,
        name: s.name,
        sublabel: s.sublabel,
      });
    });

    this.suggestionGroups.mappings.forEach((m) => {
      items.push({
        type: 'mapping',
        id: m.id,
        name: m.name,
        sublabel: m.sublabel,
      });
    });

    this.suggestionGroups.regions.forEach((r) => {
      items.push({
        type: 'region',
        id: r.id,
        name: r.name,
        sublabel: r.sublabel,
      });
    });

    this.suggestionGroups.keywordSuggestions.forEach((k) => {
      items.push({
        type: 'keyword',
        id: k.id,
        name: k.name,
        sublabel: k.sublabel,
      });
    });

    this.suggestionGroups.propertyTypes.forEach((t) => {
      items.push({
        type: 'propertyType',
        id: t.id,
        name: t.name,
        sublabel: t.sublabel,
      });
    });

    this.suggestionGroups.properties.forEach((p) => {
      items.push({
        type: 'property',
        id: p.id,
        name: p.name,
        sublabel: p.sublabel,
      });
    });

    if (this.canShowKeyword()) {
      items.push({
        type: 'keyword',
        name: this.value.trim(),
        sublabel: 'Search in title, description and features',
      });
    }

    return items;
  }

  asSelection(
    type: LocationSelectionType,
    item: LocationSuggestionItem,
  ): LocationSelection {
    return {
      type,
      id: item.id,
      name: item.name,
      sublabel: item.sublabel,
    };
  }

  isActive(index: number): boolean {
    return this.activeIndex === index;
  }

  getGroupStartIndex(group: SuggestionGroupKey): number {
    let count = 0;

    for (const key of this.suggestionOrder) {
      if (key === group) {
        break;
      }
      count += this.suggestionGroups[key].length;
    }

    return count;
  }

  hasSuggestionsBefore(group: SuggestionGroupKey): boolean {
    return this.getGroupStartIndex(group) > 0;
  }

  private hasAnySuggestions(): boolean {
    return (
      this.suggestionGroups.regions.length > 0 ||
      this.suggestionGroups.subregions.length > 0 ||
      this.suggestionGroups.propertyTypes.length > 0 ||
      this.suggestionGroups.properties.length > 0 ||
      this.suggestionGroups.mappings.length > 0 ||
      this.suggestionGroups.keywordSuggestions.length > 0
    );
  }

  private canShowKeyword(): boolean {
    if (!this.value || this.value.trim().length < this.minChars) {
      return false;
    }

    // Avoid showing generic fallback keyword row for pure numeric queries.
    // Numeric inputs are usually property IDs.
    return !this.isLikelyPropertyIdQuery(this.value);
  }

  private applyEmptyStateSuggestions(): void {
    this.suggestionGroups = this.groupSelections(this.emptyStateSuggestions);
    this.showSuggestions = this.hasAnySuggestions();
    this.activeIndex = -1;
    this.firstSuggestionChange.emit(this.getFlatSuggestions()[0] ?? null);

    if (
      this.showSuggestions &&
      this.inputElement &&
      this.suggestionsTemplate &&
      !this.overlayRef
    ) {
      this.openOverlay();
    }
  }

  private groupSelections(
    selections: LocationSelection[],
  ): LocationSuggestionGroups {
    const groups = this.emptyGroups();

    selections.forEach((selection) => {
      const item: LocationSuggestionItem = {
        id: selection.id ?? 0,
        name: selection.name,
        sublabel: selection.sublabel,
      };

      switch (selection.type) {
        case 'region':
          groups.regions.push(item);
          break;
        case 'subregion':
          groups.subregions.push(item);
          break;
        case 'propertyType':
          groups.propertyTypes.push(item);
          break;
        case 'property':
          groups.properties.push(item);
          break;
        case 'mapping':
          groups.mappings.push(item);
          break;
        case 'keyword':
          groups.keywordSuggestions.push(item);
          break;
      }
    });

    return groups;
  }

  private mergeKeywordSuggestions(
    apiKeywordSuggestions: LocationSuggestionItem[],
  ): LocationSuggestionItem[] {
    if (this.isLikelyPropertyIdQuery(this.value)) {
      return [];
    }

    const defaultKeywordSuggestions = this.emptyStateSuggestions
      .filter((selection) => selection.type === 'keyword')
      .map((selection) => ({
        id: selection.id ?? 0,
        name: selection.name,
        sublabel: selection.sublabel,
      }));

    const seen = new Set<string>();
    const merged = [
      ...apiKeywordSuggestions,
      ...defaultKeywordSuggestions,
    ].filter((item) => {
      const key = (item?.name || '').trim().toLowerCase();
      if (!key || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });

    return merged.slice(0, this.maxItems);
  }

  isLikelyPropertyIdQuery(rawValue: string): boolean {
    const value = (rawValue || '').trim();
    return /^\d+$/.test(value);
  }

  private resetSuggestions(): void {
    this.suggestionGroups = this.emptyGroups();
    this.showSuggestions = false;
    this.activeIndex = -1;
    this.closeOverlay();
    this.firstSuggestionChange.emit(null);
  }

  private emptyGroups(): LocationSuggestionGroups {
    return {
      regions: [],
      subregions: [],
      propertyTypes: [],
      properties: [],
      mappings: [],
      keywordSuggestions: [],
      keyword: null,
    };
  }

  ngOnDestroy(): void {
    this.closeOverlay();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
